import { useRef } from 'react';
import { BucketField, BucketValuesInner, Filter } from '@commerce-search-webcomponents/sdk';
import {
  Input,
  FAIcon,
  RadioButton,
  Checkbox,
  Theme,
  useTranslation,
  InputTypes,
  InputValidations,
  Tab,
  SelectorType,
  TabLayout,
  TabDesign,
} from '@nextgen-web-framework/all';
import { formatPrice } from '@nextgen-web-framework/core';
import { FacetSectionProps, FilterBucket, PriceRangeTypes } from './types/propTypes';
import styles from './Facets.module.scss';

const FacetSection = ({
  bucket,
  filterData,
  categoryAndSolutionDetails,
  priceRangeInterval,
  minPrice,
  maxPrice,
  minPriceError,
  maxPriceError,
  isPLP,
  priceRangeError,
  updateFilterData,
  checkboxOnChange,
  onMinPriceChange,
  onMaxPriceChange,
  priceInputOnClear,
}: FacetSectionProps) => {
  const t = useTranslation();
  const { locale } = PreferenceService.getPreferences();
  const { field, values } = bucket;
  const minPriceRef = useRef<HTMLInputElement>(null);
  const maxPriceRef = useRef<HTMLInputElement>(null);
  const isPriceRangeSection = field === BucketField.PRICE;
  const sectionTitle: Record<FilterBucket, string> = {
    [BucketField.BRAND]: t('search.facets.brand'),
    [BucketField.CATEGORY]: t('search.facets.category'),
    [BucketField.PRICE]: t('search.facets.price'),
    [BucketField.SORT]: t('search.facets.sort'),
    [BucketField.SOLUTION]: t('search.facets.solutions'),
  };

  const handleRadioLabelClick = (section: string, name: string, selected: boolean) => {
    const state = { ...filterData, [section]: !selected ? name : '' };
    if (section === BucketField.CATEGORY) {
      updateFilterData({ ...state, [BucketField.SOLUTION]: '' });
    } else {
      updateFilterData({ ...state, [BucketField.CATEGORY]: '' });
    }
  };

  const minPriceoOnClear = (e: React.MouseEvent) => {
    priceInputOnClear(e, PriceRangeTypes.MIN_PRICE);
    minPriceRef.current?.focus();
  };

  const maxPriceOnClear = (e: React.MouseEvent) => {
    priceInputOnClear(e, PriceRangeTypes.MAX_PRICE);
    maxPriceRef.current?.focus();
  };

  const getInputElement = ({ name = '', label = '', count = 0 }, section: keyof Filter) => {
    const selected = filterData[section] === name;
    switch (section) {
      case BucketField.SORT: {
        return (
          <RadioButton
            id={`radio-${name}`}
            label={label}
            className={styles['filter-radio']}
            checked={selected}
            onChange={() => updateFilterData({ ...filterData, [section]: name })}
            theme={selected ? Theme.PURPLE : Theme.BLACK}
          />
        );
      }
      case BucketField.PRICE: {
        const priceRange = name
          .split(' - ')
          .map((price) => formatPrice(Number(price)))
          .join(' - ');
        return (
          <Tab
            title={`${priceRange} (${count})`}
            selected={selected}
            className={styles['price-range-pill']}
            selectorType={SelectorType.CIRCLE}
            layout={TabLayout.HORIZONTAL}
            design={TabDesign.PILL}
            onTabClick={() =>
              updateFilterData({
                ...filterData,
                [section]: !selected ? name : '',
              })
            }
          />
        );
      }
      case BucketField.BRAND: {
        return (
          <Checkbox
            id={name}
            text={`${name} ${!priceRangeError ? `(${count})` : ''}`}
            className={styles['filter-checkbox']}
            theme={filterData[section]?.includes(name) ? Theme.BLUE : Theme.BLACK}
            name={name}
            checked={filterData[section]?.includes(name)}
            onChange={() => checkboxOnChange(section, name)}
          />
        );
      }
      case BucketField.CATEGORY:
      case BucketField.SOLUTION: {
        return (
          <label
            className={`${styles['filter-radio-label']} ${selected ? styles['selected'] : ''}`}
            role="presentation"
            onClick={() => handleRadioLabelClick(section, name, selected)}
          >
            {`${categoryAndSolutionDetails?.[section]?.[name]?.attribs?.[locale]?.name || name} ${!priceRangeError ? `(${count})` : ''}`}
          </label>
        );
      }
      default:
        return null;
    }
  };

  const getPriceRanges = (values: BucketValuesInner[]) => {
    const ranges: BucketValuesInner[] = [];
    let prev: BucketValuesInner = {};
    const lastEntry = { name: `${Number(values[values.length - 1].name) + Number(priceRangeInterval)}` };

    [...values, lastEntry].forEach((entry) => {
      const min = Number(prev.name);
      const max = Number(entry.name);
      if (prev.count && prev.count > 0 && !isNaN(min) && !isNaN(max)) {
        ranges.push({ name: `${min} - ${max}`, count: prev.count });
      }
      prev = entry;
    });

    return ranges;
  };

  const getFormattedPrice = (price: string) => formatPrice(Number(price)).replace(/\.00$/, '').replace(/,00$/, '');

  const getBucketValues = () => {
    if (isPLP && values && (field == BucketField.CATEGORY || field == BucketField.SOLUTION)) {
      return values.filter(({ name }) => name && categoryAndSolutionDetails?.[field]?.[name]);
    }
    return values;
  };

  const filterItems = isPriceRangeSection && values ? getPriceRanges(values) : getBucketValues();

  return (
    <section className={styles[`section-${field}`]}>
      <div className={styles['section-header']}>{sectionTitle[field as FilterBucket]}</div>
      <div className={styles['section-body']}>
        {isPriceRangeSection && (
          <>
            <div
              className={`${styles['price-range']} ${minPriceError || maxPriceError ? styles.error : styles.valid} ${priceRangeError && styles.warning}`}
            >
              <Input
                type={InputTypes.TEXT}
                name="min"
                label={t('search.facets.minValue')}
                value={minPrice && getFormattedPrice(minPrice)}
                inputIcon={{ iconName: 'close' }}
                iconClassName={styles['close-icon']}
                ref={minPriceRef}
                onChange={onMinPriceChange}
                inputIconOnClick={(e) => minPriceoOnClear(e)}
                status={minPriceError ? InputValidations.INVALID : InputValidations.DEFAULT}
                message={minPriceError ? t('search.facets.minValueErrorMsg') : ''}
              />
              <span className={styles.divider}>-</span>
              <Input
                type={InputTypes.TEXT}
                name="max"
                label={t('search.facets.maxValue')}
                value={maxPrice && getFormattedPrice(maxPrice)}
                iconClassName={styles['close-icon']}
                inputIcon={{ iconName: 'close' }}
                ref={maxPriceRef}
                onChange={onMaxPriceChange}
                inputIconOnClick={(e) => maxPriceOnClear(e)}
                status={maxPriceError ? InputValidations.INVALID : InputValidations.DEFAULT}
                message={maxPriceError ? t('search.facets.maxValueErrorMsg') : ''}
              />
            </div>
            {priceRangeError && (
              <div className={styles['price-range-error']} data-testid="price-range-error">
                <FAIcon iconName="triangle-exclamation" className={styles['price-range-warning']} />
                <span>{t('search.facets.priceRangeError')}</span>
              </div>
            )}
          </>
        )}
        <ul data-testid={`${field}-filter-list`}>
          {filterItems?.map(
            (value) =>
              (value.name || field === BucketField.SORT) && (
                <li key={value.name}>{getInputElement(value, field as keyof Filter)}</li>
              ),
          )}
        </ul>
      </div>
    </section>
  );
};

export default FacetSection;
